import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ErroApi } from '../api';
import type { Ponto } from '../api/campo';
import { plural } from '../formato';
import type { ControleDoMapa, Falha, PinoArrastavel } from '../mapaGeotecnico';
import { aoMudarTemaEfetivo, temaEfetivo } from '../tema';
import { Area, Aviso, Dialogo, ErroDaApi } from '../ui';

/**
 * A locação pelo mapa — onda 371.
 *
 * «Permita elaborar locação pelo mapa também, buscando um endereço e o usuário arrasta cada SPT/CPTu/DMT/Coleta até
 * o local desejado. A coordenada atualiza automaticamente. Também permita o ajuste manual no mapa de pontos lançados
 * com coordenadas, com justificativa.» (Humberto, 03/11/2026)
 *
 * Em cima, a busca do endereço (pelo servidor: `GET /campo/geocodificar`). No meio, o mapa com um pino por ponto
 * que tem coordenada — arrasta-se e a coordenada muda na lista ao lado; o ponto sem coordenada entra com «Pôr no
 * centro» (o centro do mapa, onde o endereço achado deixou a vista). A lista também aceita a coordenada digitada (o
 * caminho do teclado e o do navegador sem WebGL). «Gravar» manda o lote (`PATCH /os/:id/pontos/locacao`, todos ou
 * nenhum); se algum ponto que JÁ TINHA coordenada mudou, a janela pede a justificativa, que vai à trilha.
 */

const CHAVE_DO_FUNDO = 'labmanager.locacao.fundo';
const CHAVE_DAS_CAMADAS = 'labmanager.locacao.camadas';
const BLUMENAU: [number, number] = [-26.9194, -49.0661];
const ZOOM_DO_ENDERECO = 18;

interface Resultado { rotulo: string; latitude: number; longitude: number }
type Rascunho = { lat: string; lon: string };

const texto = (x: number | null) => (x == null ? '' : x.toFixed(7).replace('.', ','));
const ler = (t: string): number | null => {
  const s = t.trim().replace(',', '.');
  if (!s) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : Number.NaN;
};
const num = (s: string | null) => (s == null || s === '' ? null : Number(s));

export function LocacaoNoMapa({ osId, pontos, nomeDoTipo, aoFechar, aoGravado }: {
  osId: string;
  pontos: Ponto[];
  nomeDoTipo(tipo: string): string;
  aoFechar(): void;
  aoGravado(): Promise<void> | void;
}) {
  /* 372 (SP-371b): o suspenso também se loca, mas só se a pessoa pedir — a caixa «Incluir os suspensos» começa desligada. */
  const [comSuspensos, setComSuspensos] = useState(false);
  const suspensos = useMemo(() => pontos.filter((p) => p.situacao === 'suspenso').length, [pontos]);
  const ativos = useMemo(() => pontos.filter((p) => p.situacao !== 'cancelado'
    && (comSuspensos || p.situacao !== 'suspenso')), [pontos, comSuspensos]);
  const caixa = useRef<HTMLDivElement>(null);
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  const [semMapa, setSemMapa] = useState(false);
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<Resultado[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [janela, setJanela] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [feito, setFeito] = useState<string | null>(null);

  const atual = (p: Ponto): Rascunho => rascunhos[p.id] ?? { lat: texto(num(p.latitude)), lon: texto(num(p.longitude)) };
  const linhas = ativos.map((p) => {
    const r = atual(p);
    const lat = ler(r.lat);
    const lon = ler(r.lon);
    const valida = lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon)
      && lat >= -34 && lat <= 6 && lon >= -74 && lon <= -28;
    const vazia = lat == null && lon == null;
    const tinha = p.latitude != null;
    const mudou = valida && (!tinha || Math.abs(lat! - Number(p.latitude)) > 1e-7 || Math.abs(lon! - Number(p.longitude)) > 1e-7);
    const erroDaLinha = !valida && !vazia ? 'latitude e longitude em graus, no Brasil (ex.: -26,9194 e -49,0661) — confira o sinal'
      : vazia && tinha ? 'a coordenada lançada não se apaga por aqui' : null;
    return { p, r, lat, lon, valida, tinha, mudou, erroDaLinha };
  });
  const mudadas = linhas.filter((l) => l.mudou && !l.erroDaLinha);
  const ajustes = mudadas.filter((l) => l.tinha);
  const comErro = linhas.filter((l) => l.erroDaLinha).length;

  /* O mapa (sob demanda, como as outras telas do motor). */
  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligarTema: (() => void) | null = null;
    void (async () => {
      const { criarMapa } = await import('../mapaGeotecnico');
      if (!vivo || !caixa.current) return;
      const comCoordenada = ativos.find((p) => p.latitude != null);
      const m = await criarMapa(caixa.current, {
        casa: comCoordenada ? [Number(comCoordenada.latitude), Number(comCoordenada.longitude)] : BLUMENAU,
        chaveDoFundo: CHAVE_DO_FUNDO,
        chaveDasCamadas: CHAVE_DAS_CAMADAS,
        tema: temaEfetivo(),
        tamanhoDoIcone: 30,
        dica: () => '',
        textoDaLista: (f) => f.nome,
      });
      if (!vivo) { m.destruir(); return; }
      criado = m;
      m.aoFalhar((f: Falha, falhou) => { if (falhou && (f === 'webgl' || f === 'motor')) setSemMapa(true); });
      desligarTema = aoMudarTemaEfetivo((t) => m.definirTema(t));
      if (comCoordenada) m.irPara({ lat: Number(comCoordenada.latitude), lng: Number(comCoordenada.longitude) }, 17);
      setControle(m);
    })().catch(() => { if (vivo) setSemMapa(true); });
    return () => { vivo = false; desligarTema?.(); criado?.destruir(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Os pinos: um por ponto com coordenada válida (a gravada ou a nova). */
  const chaveDosPinos = linhas.map((l) => `${l.p.id}:${l.r.lat}:${l.r.lon}:${l.mudou}`).join('|');
  useEffect(() => {
    if (!controle) return;
    const pinos: PinoArrastavel[] = linhas.filter((l) => l.valida)
      .map((l) => ({ chave: l.p.id, rotulo: l.p.codigo, lat: l.lat!, lng: l.lon!, mudou: l.mudou }));
    controle.definirPinos(pinos, (chave, q) => {
      setFeito(null);
      setRascunhos((antes) => ({ ...antes, [chave]: { lat: texto(q.lat), lon: texto(q.lng) } }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controle, chaveDosPinos]);

  const mudar = (id: string, parte: Partial<Rascunho>) => {
    setFeito(null);
    const p = ativos.find((x) => x.id === id)!;
    setRascunhos((antes) => ({ ...antes, [id]: { ...atual(p), ...parte } }));
  };

  const porNoCentro = (id: string) => {
    const c = controle?.centro();
    if (!c) return;
    mudar(id, { lat: texto(c.lat), lon: texto(c.lng) });
  };

  const buscar = async () => {
    if (busca.trim().length < 3) return;
    setBuscando(true); setErro(null);
    try {
      setResultados(await api<Resultado[]>(`/campo/geocodificar?q=${encodeURIComponent(busca.trim())}`));
    } catch (e) { setErro(e); setResultados(null); } finally { setBuscando(false); }
  };
  const irAo = (r: Resultado) => {
    controle?.irPara({ lat: r.latitude, lng: r.longitude }, ZOOM_DO_ENDERECO);
    setResultados(null);
    setBusca(r.rotulo);
  };

  const motivoDoGravar = comErro ? `Corrija ${plural(comErro, 'ponto', 'pontos')} antes de gravar.`
    : mudadas.length === 0 ? 'Arraste um pino (ou ponha um ponto no centro) para locar.' : null;
  const rotulo = mudadas.length === 1 ? 'Gravar 1 ponto' : mudadas.length ? `Gravar os ${mudadas.length} pontos` : 'Gravar';
  const curto = motivo.trim().length < 5;

  const enviar = async (comMotivo: boolean) => {
    if (ocupado) return;
    setOcupado(true); setErro(null);
    try {
      const r = await api<{ gravados: number }>(`/os/${osId}/pontos/locacao`, {
        metodo: 'PATCH',
        corpo: {
          pontos: mudadas.map((l) => ({ id: l.p.id, latitude: l.lat, longitude: l.lon })),
          ...(comMotivo ? { motivo: motivo.trim() } : {}),
        },
      });
      setJanela(false); setMotivo('');
      /* Recarrega antes de limpar os rascunhos: o pino não volta um instante para a coordenada antiga. */
      await aoGravado();
      setRascunhos({});
      setFeito(`${plural(r.gravados, 'ponto gravado', 'pontos gravados')}.`);
    } catch (e) {
      setErro(e);
      if (e instanceof ErroApi && !e.campos.length) setJanela(false);
    } finally { setOcupado(false); }
  };
  const gravar = () => {
    if (motivoDoGravar) return;
    if (ajustes.length) { setErro(null); setJanela(true); } else void enviar(false);
  };

  return (
    <section className="cartao locacao" aria-label="Locação pelo mapa">
      <div className="cartao-cabeca">
        <h2>Locação pelo mapa</h2>
        <span className="sub cresce">
          {plural(ativos.length, 'ponto', 'pontos')} · {plural(ativos.filter((p) => p.latitude == null).length, 'sem coordenada', 'sem coordenada')}
        </span>
        <button type="button" className="botao secundario" onClick={aoFechar}>Fechar</button>
      </div>
      <form className="locacao-busca" role="search" onSubmit={(e) => { e.preventDefault(); void buscar(); }}>
        <input type="search" className="campo" value={busca} placeholder="Endereço da obra (rua, número, cidade)"
          aria-label="Buscar o endereço" onChange={(e) => setBusca(e.target.value)} />
        <button type="submit" className="botao secundario" aria-disabled={busca.trim().length < 3 || buscando}>
          {buscando ? 'Buscando…' : 'Buscar'}
        </button>
      </form>
      {resultados && (
        resultados.length === 0 ? <p className="sub">Nenhum endereço encontrado. Tente com a cidade.</p> : (
          <ul className="locacao-resultados">
            {resultados.map((r, i) => (
              <li key={i}><button type="button" className="ligacao" onClick={() => irAo(r)}>{r.rotulo}</button></li>
            ))}
            <li className="sub">Endereços: © colaboradores do OpenStreetMap (Nominatim).</li>
          </ul>
        )
      )}
      <ErroDaApi erro={erro} />
      {feito && <p className="sub" role="status">{feito}</p>}
      <div className="locacao-meio">
        <div className="locacao-mapa">
          <div ref={caixa} className="mapa-caixa locacao-mapa-caixa" />
          {semMapa && (
            <Aviso tipo="info" titulo="Este navegador não desenha o mapa (sem WebGL).">
              Digite a latitude e a longitude na lista ao lado.
            </Aviso>
          )}
        </div>
        <div className="locacao-lista">
          <p className="sub">Arraste o pino até o lugar do ponto. Sem coordenada, «Pôr no centro» o leva ao centro do mapa.
            A coordenada locada no mapa sai «estimada» no boletim e no relatório.</p>
          {suspensos > 0 && (
            <label className="caixa">
              <input type="checkbox" checked={comSuspensos} onChange={(e) => setComSuspensos(e.target.checked)} />
              Incluir os suspensos ({suspensos})
            </label>
          )}
          <table className="tf-tabela">
            <thead><tr><th scope="col">Ponto</th><th scope="col">Latitude</th><th scope="col">Longitude</th></tr></thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.p.id} className={l.mudou ? 'locacao-mudou' : undefined}>
                  <th scope="row">
                    <strong className="mono">{l.p.codigo}</strong>
                    <span className="sub">{nomeDoTipo(l.p.tipo)}</span>
                    {l.mudou && <span className="selo atencao">{l.tinha ? 'Ajustado' : 'Locado'}</span>}
                    {!l.mudou && l.p.coordenada_pelo_mapa && <span className="selo neutro" title="Coordenada estimada pela locação no mapa">Estimada</span>}
                    {l.p.situacao === 'suspenso' && <span className="selo neutro">Suspenso</span>}
                    {!l.valida && !l.tinha && !semMapa && (
                      <button type="button" className="ligacao" data-por={l.p.id} disabled={!controle}
                        onClick={() => porNoCentro(l.p.id)}>Pôr no centro</button>
                    )}
                  </th>
                  <td>
                    <input className="campo mono" inputMode="decimal" data-lat={l.p.id} value={l.r.lat}
                      aria-label={`Latitude de ${l.p.codigo}`} aria-invalid={l.erroDaLinha ? true : undefined}
                      onChange={(e) => mudar(l.p.id, { lat: e.target.value })} />
                  </td>
                  <td>
                    <input className="campo mono" inputMode="decimal" data-lon={l.p.id} value={l.r.lon}
                      aria-label={`Longitude de ${l.p.codigo}`} aria-invalid={l.erroDaLinha ? true : undefined}
                      onChange={(e) => mudar(l.p.id, { lon: e.target.value })} />
                    {l.erroDaLinha && <span className="erro">{l.erroDaLinha}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="locacao-acoes">
            {mudadas.length > 0 && (
              <button type="button" className="botao secundario" onClick={() => { setRascunhos({}); setFeito(null); }}>Desfazer</button>
            )}
            <button type="button" className="botao" aria-disabled={motivoDoGravar || ocupado ? true : undefined}
              aria-describedby={motivoDoGravar ? 'locacao-motivo-gravar' : undefined} onClick={gravar}>
              {ocupado && !janela ? 'Gravando…' : rotulo}
            </button>
            {motivoDoGravar && <small id="locacao-motivo-gravar" className="sub">{motivoDoGravar}</small>}
          </div>
        </div>
      </div>

      {janela && (
        <Dialogo aberto titulo="Justificar o ajuste da coordenada" rotulo="Locação pelo mapa" aoFechar={() => setJanela(false)}>
          <form onSubmit={(e) => { e.preventDefault(); if (!curto) void enviar(true); }}>
            <ErroDaApi erro={erro} />
            <p className="sub">
              {ajustes.length === 1 ? `O ponto ${ajustes[0].p.codigo} já tinha coordenada lançada.`
                : `Os pontos ${ajustes.map((l) => l.p.codigo).join(', ')} já tinham coordenada lançada.`}{' '}
              A justificativa vai à trilha de cada ponto gravado neste lote.
            </p>
            <Area rotulo="Justificativa do ajuste" rows={2} value={motivo} maxLength={2000}
              dica="Ex.: o furo foi aberto 10 m ao sul, por causa do acesso." onChange={(e) => setMotivo(e.target.value)} />
            {curto && <p className="sub" role="status">A justificativa tem 5 caracteres ou mais.</p>}
            <div className="rodape-dialogo">
              <button type="submit" className="botao" disabled={curto || ocupado}>{ocupado ? 'Gravando…' : rotulo}</button>
              <button type="button" className="botao secundario" onClick={() => setJanela(false)}>Cancelar</button>
            </div>
          </form>
        </Dialogo>
      )}
    </section>
  );
}
